import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/** Paths that stay inside the SafeGeo SPA (React Router). */
export function isAppPath(href: string | undefined | null): boolean {
  if (!href) return false;
  if (href.startsWith("http") || href.startsWith("mailto:")) return false;
  if (href.startsWith("shopify://")) return false;
  if (href.startsWith("#")) return false;
  return href.startsWith("/");
}

function closestNavHost(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  return target.closest("s-button, s-link, a");
}

/**
 * Sync App Bridge + Polaris href clicks → React Router.
 * Mount once in AppShell - fixes s-button/s-link href="/app/…" full reloads
 * and missed SPA transitions inside the admin iframe.
 */
export function useAppBridgeRouterSync() {
  const navigate = useNavigate();

  useEffect(() => {
    const onShopifyNavigate = (event: Event) => {
      const detail = (event as CustomEvent<{ pathname?: string; path?: string }>)
        .detail;
      const path = detail?.pathname || detail?.path;
      if (!path || !isAppPath(path)) return;
      event.preventDefault();
      navigate(path + (window.location.search || ""));
    };

    const onClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const host = closestNavHost(event.target);
      if (!host) return;
      if (host.hasAttribute("disabled")) return;
      const href = host.getAttribute("href");
      if (!href || !isAppPath(href)) return;
      // External-looking targets leave the iframe alone.
      const target = host.getAttribute("target");
      if (target === "_blank" || target === "_top") return;
      event.preventDefault();
      event.stopPropagation();
      navigate(href);
    };

    const onPopState = () => {
      navigate(window.location.pathname + window.location.search, {
        replace: true,
      });
    };

    document.addEventListener(
      "shopify:navigate",
      onShopifyNavigate as EventListener,
    );
    document.addEventListener("click", onClickCapture, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener(
        "shopify:navigate",
        onShopifyNavigate as EventListener,
      );
      document.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate]);
}
